import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { DEMO_AUDIT_EVENTS, DEMO_APPROVALS, DEMO_COMPOSITE_HEALTH, DEMO_HEALTH, DEMO_LIST_PRODUCTS_RESPONSE, DEMO_PRODUCTS, DEMO_SUBSCRIPTIONS, } from "./demo-fixtures.js";
function readBffUrl() {
    const viteEnv = import.meta.env;
    if (viteEnv?.VITE_BFF_URL)
        return viteEnv.VITE_BFF_URL;
    if (typeof process !== "undefined" && process.env.VITE_BFF_URL) {
        return process.env.VITE_BFF_URL;
    }
    return "http://localhost:8080";
}
function readDemoMode() {
    const viteEnv = import.meta.env;
    if (viteEnv?.VITE_DEMO_MODE === "true" || viteEnv?.VITE_DEMO_MODE === "1")
        return true;
    if (typeof process !== "undefined" && (process.env.VITE_DEMO_MODE === "true" || process.env.VITE_DEMO_MODE === "1")) {
        return true;
    }
    return false;
}
const BFF_URL = readBffUrl();
const DEMO_MODE = readDemoMode();
/**
 * demoBaseQuery returns canned fixtures for every endpoint the portals
 * consume. Toggle via VITE_DEMO_MODE=true at build time. Lets the 5
 * static-site portals run in production without a live BFF — useful for
 * marketing demos, design reviews, and smoke-checking deploys before
 * the real backend ships per-portal.
 *
 * Mutation endpoints (createProduct, transitionProduct, decideApproval)
 * return a synthesized response shape but do NOT mutate the fixtures —
 * the next query call returns the original list. This is intentional:
 * demo mode is read-mostly; round-tripping mutations would require a
 * client-side store and feels misleading for a demo deployment.
 */
function demoQuery(url, method, body) {
    const path = url.split("?")[0] ?? url;
    if (path === "/health")
        return DEMO_HEALTH;
    if (path === "/healthz/composite")
        return DEMO_COMPOSITE_HEALTH;
    if (path === "/v1/products") {
        if (method === "POST" && body && typeof body === "object") {
            const input = body;
            return {
                ...DEMO_PRODUCTS[0],
                id: "demo-new-product",
                code: input.code ?? "DEMO-CODE",
                name: input.name ?? "Demo Product",
                product_type: input.product_type ?? "short_duration_credit",
                status: "pending",
                allowed_next: ["approved", "cancelled"],
            };
        }
        return DEMO_LIST_PRODUCTS_RESPONSE;
    }
    const productMatch = /^\/v1\/products\/([^/]+)(\/transition)?$/.exec(path);
    if (productMatch) {
        const id = decodeURIComponent(productMatch[1]);
        const isTransition = Boolean(productMatch[2]);
        const product = DEMO_PRODUCTS.find((p) => p.id === id) ?? DEMO_PRODUCTS[0];
        if (isTransition && method === "POST" && body && typeof body === "object") {
            const to = body.to ?? product.status;
            return { ...product, status: to };
        }
        return product;
    }
    if (path === "/v1/audit/events")
        return DEMO_AUDIT_EVENTS;
    if (path === "/v1/approvals")
        return DEMO_APPROVALS;
    const approvalDecide = /^\/v1\/approvals\/([^/]+)\/decide$/.exec(path);
    if (approvalDecide) {
        const id = decodeURIComponent(approvalDecide[1]);
        const decision = body && typeof body === "object" ? body.status ?? "approved" : "approved";
        const approval = DEMO_APPROVALS.find((a) => a.id === id) ?? DEMO_APPROVALS[0];
        return { ...approval, status: decision, decided_at: "2026-04-26T00:00:00.000000Z" };
    }
    if (path === "/v1/subscriptions" && method === "POST") {
        // Pretends to create a subscription but actually returns the
        // pre-baked pending subscription so the redirect lands on a
        // detail page with a populated audit timeline. Form values are
        // echoed in the returned object so the optimistic UI feels right;
        // the persistent state is the fixture in DEMO_SUBSCRIPTIONS.
        const input = body && typeof body === "object"
            ? body
            : {};
        return {
            id: "cccccccc-1111-4ccc-8ccc-000000000002",
            product_id: input.product_id ?? DEMO_PRODUCTS[2].id,
            investor_user_id: input.investor_user_id ?? "dddddddd-1111-4ddd-8ddd-000000000002",
            amount_minor: input.amount_minor ?? 25_000_000_000,
            currency: input.currency ?? "USD",
            status: "pending",
            created_at: "2026-04-26T09:00:00.000000Z",
            updated_at: "2026-04-26T09:00:00.000000Z",
        };
    }
    const subMatch = /^\/v1\/subscriptions\/([^/]+)$/.exec(path);
    if (subMatch) {
        const id = decodeURIComponent(subMatch[1]);
        return DEMO_SUBSCRIPTIONS[id] ?? Object.values(DEMO_SUBSCRIPTIONS)[0];
    }
    // Unknown path — return empty object so RTK Query's onSuccess fires.
    return {};
}
const realBaseQuery = fetchBaseQuery({ baseUrl: BFF_URL });
const demoBaseQuery = async (args) => {
    const url = typeof args === "string" ? args : args.url;
    const method = typeof args === "string" ? "GET" : (args.method ?? "GET");
    const body = typeof args === "string" ? undefined : args.body;
    await new Promise((r) => setTimeout(r, 60)); // simulate network for skeleton states
    const data = demoQuery(url, method, body);
    return { data };
};
const baseQuery = DEMO_MODE ? demoBaseQuery : realBaseQuery;
export const isDemoMode = () => DEMO_MODE;
export const hydraxApi = createApi({
    reducerPath: "hydraxApi",
    baseQuery,
    endpoints: (builder) => ({
        getHealth: builder.query({
            query: () => "/health",
        }),
        getHealthzComposite: builder.query({
            query: () => "/healthz/composite",
        }),
        createProduct: builder.mutation({
            query: (body) => ({ url: "/v1/products", method: "POST", body }),
        }),
        listProducts: builder.query({
            query: (args) => {
                const params = new URLSearchParams();
                if (args && args.limit !== undefined)
                    params.set("limit", String(args.limit));
                if (args && args.offset !== undefined)
                    params.set("offset", String(args.offset));
                const qs = params.toString();
                return qs ? `/v1/products?${qs}` : "/v1/products";
            },
        }),
        getProduct: builder.query({
            query: (id) => ({ url: `/v1/products/${encodeURIComponent(id)}` }),
        }),
        transitionProduct: builder.mutation({
            query: ({ id, to }) => ({
                url: `/v1/products/${encodeURIComponent(id)}/transition`,
                method: "POST",
                body: { to },
            }),
        }),
        listAuditEvents: builder.query({
            query: ({ tenant_id, resource_type, resource_id }) => `/v1/audit/events?tenant_id=${encodeURIComponent(tenant_id)}&resource_type=${encodeURIComponent(resource_type)}&resource_id=${encodeURIComponent(resource_id)}`,
        }),
        listPendingApprovals: builder.query({
            query: () => "/v1/approvals",
        }),
        decideApproval: builder.mutation({
            query: ({ id, status, decided_by_user_id }) => ({
                url: `/v1/approvals/${encodeURIComponent(id)}/decide`,
                method: "POST",
                body: { status, decided_by_user_id },
            }),
        }),
        getSubscription: builder.query({
            query: (id) => ({ url: `/v1/subscriptions/${encodeURIComponent(id)}` }),
        }),
        createSubscription: builder.mutation({
            query: (body) => ({ url: "/v1/subscriptions", method: "POST", body }),
        }),
    }),
});
export const useGetHealthQuery = hydraxApi.endpoints.getHealth.useQuery;
export const useGetHealthzCompositeQuery = hydraxApi.endpoints.getHealthzComposite.useQuery;
export const useCreateProductMutation = hydraxApi.endpoints.createProduct.useMutation;
export const useGetProductQuery = hydraxApi.endpoints.getProduct.useQuery;
export const useListProductsQuery = hydraxApi.endpoints.listProducts.useQuery;
export const useTransitionProductMutation = hydraxApi.endpoints.transitionProduct.useMutation;
export const useListAuditEventsQuery = hydraxApi.endpoints.listAuditEvents.useQuery;
export const useListPendingApprovalsQuery = hydraxApi.endpoints.listPendingApprovals.useQuery;
export const useDecideApprovalMutation = hydraxApi.endpoints.decideApproval.useMutation;
export const useGetSubscriptionQuery = hydraxApi.endpoints.getSubscription.useQuery;
export const useCreateSubscriptionMutation = hydraxApi.endpoints.createSubscription.useMutation;
//# sourceMappingURL=api.js.map